// Presence wire helpers (R16 W-B). BOTH paths here are the documented
// raw-fetch exception to the "always apiClient" rule: presence is a
// best-effort beacon, and the apiClient 401→renew middleware hard
// redirects to /login when renew fails (src/lib/api/middleware.ts).
// A transient 401 on a heartbeat must NEVER yank a logged-in user (or a
// mocked e2e session) to the login page — legacy traders-online-counter
// used raw fetch with a swallow-all catch for exactly this reason.
//
// CSRF decision: `navigator.sendBeacon` cannot attach headers, and the
// backend double-submit middleware (backend/src/quartel/api/middleware/
// csrf.py) rejects header-less POSTs with 403. Instead of exempting
// /api/v2/traders-online server-side (which would let any third-party
// page flip a logged-in browser's presence), both paths attach the
// token read SYNCHRONOUSLY from the cookie; leave adds
// `keepalive: true` (legacy fallback branch) because the tab may be
// closing.
import { readCsrfCookie } from "@/lib/api/csrf";

export const PRESENCE_ENDPOINT = "/api/v2/traders-online";

interface PresenceCountPayload {
  count?: number;
}

/** POST one heartbeat; returns the live count or null on any failure. */
export async function postPresenceHeartbeat(): Promise<number | null> {
  const token = readCsrfCookie();
  // No CSRF cookie means no authenticated session context — the backend
  // would 403 the POST anyway, so skip instead of burning a request.
  if (!token) return null;
  try {
    const res = await fetch(PRESENCE_ENDPOINT, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": token },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const raw = (await res.json()) as PresenceCountPayload;
    return typeof raw.count === "number" ? raw.count : null;
  } catch {
    return null;
  }
}

/** Best-effort leave — fire-and-forget, survives tab close via keepalive. */
export function sendPresenceLeave(): void {
  const token = readCsrfCookie();
  if (!token) return;
  void fetch(PRESENCE_ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "X-CSRF-Token": token,
    },
    body: JSON.stringify({ action: "leave" }),
    cache: "no-store",
    keepalive: true,
  }).catch(() => undefined);
}
